import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TaskService } from '../../services/task.service';
import { Subscription } from 'rxjs';
import { Task } from '../../models/task.model';
import { Priority } from '../../models/priority.model';
import { TaskCommentReadStateService } from '../../services/task-comment-read-state.service';

@Component({
    selector: 'app-task-counter-card',
    templateUrl: './task-counter-card.component.html',
    styleUrls: ['./task-counter-card.component.scss'],
    standalone: false
})
export class TaskCounterCardComponent implements OnInit, OnDestroy {
    @Input() public shouldRefresh = false;
    @Output() clicked = new EventEmitter<any>();

    public isLoading = false;
    public showError = false;

    public numberOfTasks: number = 0;
    /** Count of open tasks that have comments the user hasn't seen (device-local). */
    public numberOfTasksWithNewComments: number = 0;
    protected tasks: Task[] = [];
    protected readonly Math = Math;
    private taskService: TaskService = inject(TaskService);
    private commentReadState: TaskCommentReadStateService = inject(TaskCommentReadStateService);
    private taskSubscription: Subscription | undefined;
    // Matches the CSS breakpoint used by `.desktop-only` / `.mobile-only`
    // so the tasks list is only fetched when the desktop template is visible.
    private readonly desktopViewport: MediaQueryList = window.matchMedia('(min-width: 768px)');

    public get numberOfOpenTasks(): number {
        return this.numberOfTasks;
    }

    public async ngOnInit() {
        await this.loadTasks();
    }

    public emitClick() {
        this.clicked.emit();
    }

    ngOnDestroy() {
        if (this.taskSubscription) {
            this.taskSubscription.unsubscribe();
        }
    }

    public async loadTasks() {
        this.isLoading = true;
        try {

            this.taskService.getNumberOfOpenTasks(); // Trigger the task count update
            this.taskSubscription = this.taskService.numberOfOpenTasks$.subscribe({
                next: numberOfTasks => {
                    this.numberOfTasks = numberOfTasks;
                    this.isLoading = false;

                },
                error: error => {
                    console.warn('DashboardPage: ionViewWillEnter hook failed because of an error', error);
                    this.showError = true;
                    this.isLoading = false;

                }
            });

            // Fetch open tasks (with their comments) to compute the dashboard
            // "new comments" signal on every viewport. The full list is only
            // rendered on desktop, so we avoid populating `this.tasks` on mobile
            // (which would render hidden rows).
            const openTasks = (await this.taskService.getTasks(true)).map(taskData =>
                Object.assign(new Task(), taskData)
            );
            this.numberOfTasksWithNewComments = openTasks.filter(task =>
                this.commentReadState.hasNewComments(task)
            ).length;

            if (this.desktopViewport.matches) {
                this.tasks = openTasks;
                // sort tasks by priority
                this.sortTasks();
            }
        } catch (error) {
            this.isLoading = false;

            console.warn(`TaskCounterCardComponent: `, error);
            throw error;
        }
    }

    protected sortTasks(): void {

        this.tasks.sort((a, b) => {
            if (a.priority === b.priority) {
                if (a.createdAt === b.createdAt) {
                    return a.id - b.id;
                }
                return a.createdAt > b.createdAt ? 1 : -1;
            }
            return this.convertPriorityToNumber(a.priority) - this.convertPriorityToNumber(b.priority);
        });

    }

    private convertPriorityToNumber(priority: Priority): number {
        // TODO 18/07/2024 Roy Schrauwen: There has to be a cleaner solution than doing this...
        switch (priority) {
            case Priority.VeryHigh:
                return 0;
            case Priority.High:
                return 1;
            case Priority.Medium:
                return 2;
            case Priority.Low:
                return 3;
            case Priority.VeryLow:
                return 4;
            default:
                return 2;
        }
    }

}
